import { Injectable, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router, NavigationEnd, ActivationEnd, Params } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, takeUntil, filter } from 'rxjs';
import { LostSaleFilters, LostSaleTabType, LostSaleItem, LostSaleSummaryItem } from '../models/lost-sale.interfaces';

@Injectable()
export class LostSaleV2StateService implements OnDestroy {
  private destroy$ = new Subject<void>();

  private filtersSubject = new BehaviorSubject<LostSaleFilters>(this.getDefaultFilters());
  private stagedFiltersSubject = new BehaviorSubject<LostSaleFilters>(this.getDefaultFilters());
  private searchTermSubject = new BehaviorSubject<string>('');
  private activeTabSubject = new BehaviorSubject<LostSaleTabType>(LostSaleTabType.COMMERCIAL);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private queryParamsChangedSubject = new Subject<void>();
  private isInitializing = true;
  private commercialDataSubject = new BehaviorSubject<LostSaleItem[]>([]);
  private summaryDataSubject = new BehaviorSubject<LostSaleSummaryItem[]>([]);
  private commercialTotalSubject = new BehaviorSubject<number>(0);
  private summaryTotalSubject = new BehaviorSubject<number>(0);

  private commercialPageSubject = new BehaviorSubject<{ pageIndex: number; pageSize: number }>({
    pageIndex: 0,
    pageSize: 100
  });
  private summaryPageSubject = new BehaviorSubject<{ pageIndex: number; pageSize: number }>({
    pageIndex: 0,
    pageSize: 100
  });

  private commercialSortSubject = new BehaviorSubject<{ active: string; direction: 'asc' | 'desc' | '' }>({
    active: 'createdAt',
    direction: 'desc'
  });
  private summarySortSubject = new BehaviorSubject<{ active: string; direction: 'asc' | 'desc' | '' }>({
    active: 'materialName',
    direction: 'asc'
  });
  public filters$ = this.filtersSubject.asObservable();
  public stagedFilters$ = this.stagedFiltersSubject.asObservable();
  public searchTerm$ = this.searchTermSubject.asObservable();
  public activeTab$ = this.activeTabSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public queryParamsChanged$ = this.queryParamsChangedSubject.asObservable();
  public commercialData$ = this.commercialDataSubject.asObservable();
  public summaryData$ = this.summaryDataSubject.asObservable();
  public commercialTotal$ = this.commercialTotalSubject.asObservable();
  public summaryTotal$ = this.summaryTotalSubject.asObservable();
  public commercialPage$ = this.commercialPageSubject.asObservable();
  public summaryPage$ = this.summaryPageSubject.asObservable();
  public commercialSort$ = this.commercialSortSubject.asObservable();
  public summarySort$ = this.summarySortSubject.asObservable();

  public filterForm: FormGroup;

  constructor(
    private router: Router
  ) {
    this.filterForm = this.createFilterForm();

    const urlTree = this.router.parseUrl(this.router.url);
    const params = urlTree.queryParams;

    if (this.urlHasFilterParams(params)) {
      this.resetAndCleanUrl(params);
    } else {
      this.loadStateFromQueryParams(params);
    }

    this.setupFilterSync();
    this.subscribeToRouterEvents();
  }

  private urlHasFilterParams(params: Params): boolean {
    return (
      params['startDate'] !== undefined ||
      params['companyId'] !== undefined ||
      params['salesOfficeId'] !== undefined ||
      params['subRouteId'] !== undefined ||
      params['assignedUserId'] !== undefined ||
      params['warehouseId'] !== undefined ||
      params['materialGroupId'] !== undefined ||
      params['materialId'] !== undefined ||
      params['customerId'] !== undefined ||
      params['salesGroup'] !== undefined ||
      params['salesZone'] !== undefined
    );
  }

  private loadStateFromQueryParams(params: Params): void {
    this.setActiveTabFromCurrentUrl();

    const filters = this.parseFiltersFromQueryParams(params) || this.getDefaultFilters();
    this.filtersSubject.next(filters);
    this.stagedFiltersSubject.next(filters);
    this.filterForm.patchValue(this.mapFiltersToForm(filters), { emitEvent: false });

    if (params['search']) {
      this.searchTermSubject.next(params['search']);
    }

    if (params['page'] || params['pageSize']) {
      const pageIndex = params['page'] ? parseInt(params['page'], 10) - 1 : 0;
      const pageSize = params['pageSize'] ? parseInt(params['pageSize'], 10) : 100;
      const currentTab = this.activeTabSubject.value;

      if (currentTab === LostSaleTabType.COMMERCIAL) {
        this.commercialPageSubject.next({ pageIndex, pageSize });
      } else {
        this.summaryPageSubject.next({ pageIndex, pageSize });
      }
    }

    this.isInitializing = false;
    setTimeout(() => this.queryParamsChangedSubject.next(), 0);
  }

  private subscribeToRouterEvents(): void {
    let previousTab = this.activeTabSubject.value;
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      const currentUrl = event.urlAfterRedirects;
      let currentTab = this.activeTabSubject.value;

      if (currentUrl.includes('/lost-sale-v2/commercial')) {
        currentTab = LostSaleTabType.COMMERCIAL;
        this.activeTabSubject.next(LostSaleTabType.COMMERCIAL);
      } else if (currentUrl.includes('/lost-sale-v2/summary')) {
        currentTab = LostSaleTabType.SUMMARY;
        this.activeTabSubject.next(LostSaleTabType.SUMMARY);
      }

      const urlTree = this.router.parseUrl(currentUrl);
      const params = urlTree.queryParams;
      const filters = this.parseFiltersFromQueryParams(params) || this.getDefaultFilters();

      const isTabChange = currentTab !== previousTab;
      previousTab = currentTab;

      if (!this.isInitializing) {
        setTimeout(() => this.queryParamsChangedSubject.next(), 0);
      }
    });
  }

  private resetAndCleanUrl(params: Params): void {
    const defaultFilters = this.getDefaultFilters();
    this.filterForm.patchValue(this.mapFiltersToForm(defaultFilters), { emitEvent: false });
    this.filtersSubject.next(defaultFilters);
    this.stagedFiltersSubject.next(defaultFilters);

    const queryParams: Params = {};

    this.router.navigate([], {
      relativeTo: this.router.routerState.root,
      queryParams: queryParams,
      replaceUrl: true
    }).then(() => {
      this.isInitializing = false;
      this.queryParamsChangedSubject.next();
    });

    this.resetFilters();
  }

  private createFilterForm(): FormGroup {
    const defaultFilters = this.getDefaultFilters();

    return new FormGroup({
      start: new FormControl(defaultFilters.startDate),
      end: new FormControl(defaultFilters.endDate),
      companyId: new FormControl(defaultFilters.companyId),
      salesOfficeId: new FormControl(defaultFilters.salesOfficeId),
      subRouteId: new FormControl(defaultFilters.subRouteId || []),
      assignedUserId: new FormControl(defaultFilters.assignedUserId || []),
      warehouseId: new FormControl(defaultFilters.warehouseId || []),
      materialGroupId: new FormControl(defaultFilters.materialGroupId || []),
      materialId: new FormControl(defaultFilters.materialId),
      customerId: new FormControl(defaultFilters.customerId || []),
      // FS-37: Campos de Usuario de la Cadena de Distribución del soldTo.
      // Controles de texto simple (un solo valor); se convierten a array en mapFormToFilters().
      salesGroup: new FormControl(defaultFilters.salesGroup?.[0] || ''),
      salesZone: new FormControl(defaultFilters.salesZone?.[0] || ''),

      // Search form controls for mat-select-search components
      searchAssignedUser: new FormControl(''),
      searchReceivingBusinessPartner: new FormControl('')
    });
  }

  private getDefaultFilters(): LostSaleFilters {
    const today = new Date();
    const oneDayAgo = new Date();
    oneDayAgo.setDate(today.getDate() - 1);

    return {
      startDate: oneDayAgo,
      endDate: today,
      companyId: undefined,
      salesOfficeId: undefined,
      subRouteId: [],
      assignedUserId: [],
      warehouseId: [],
      materialGroupId: [],
      materialId: undefined,
      customerId: [],
      salesGroup: [],
      salesZone: []
    };
  }

  private setActiveTabFromCurrentUrl(): void {
    const currentUrl = this.router.url;

    if (currentUrl.includes('/lost-sale-v2/commercial')) {
      this.activeTabSubject.next(LostSaleTabType.COMMERCIAL);
    } else if (currentUrl.includes('/lost-sale-v2/summary')) {
      this.activeTabSubject.next(LostSaleTabType.SUMMARY);
    } else {
      this.activeTabSubject.next(LostSaleTabType.COMMERCIAL);
    }
  }

  // private initializeFromQueryParams(): void {
  //   this.setActiveTabFromCurrentUrl();
  //   let previousTab = this.activeTabSubject.value;
  //   this.router.events.pipe(
  //     filter(event => event instanceof NavigationEnd),
  //     takeUntil(this.destroy$)
  //   ).subscribe((event: NavigationEnd) => {
  //     const currentUrl = event.urlAfterRedirects;
  //     let currentTab = this.activeTabSubject.value;

  //     if (currentUrl.includes('/lost-sale-v2/commercial')) {
  //       currentTab = LostSaleTabType.COMMERCIAL;
  //       this.activeTabSubject.next(LostSaleTabType.COMMERCIAL);
  //     } else if (currentUrl.includes('/lost-sale-v2/summary')) {
  //       currentTab = LostSaleTabType.SUMMARY;
  //       this.activeTabSubject.next(LostSaleTabType.SUMMARY);
  //     }

  //     const urlTree = this.router.parseUrl(currentUrl);
  //     const params = urlTree.queryParams;
  //     const filters = this.parseFiltersFromQueryParams(params) || this.getDefaultFilters();

  //     this.filtersSubject.next(filters);
  //     this.stagedFiltersSubject.next(filters);
  //     this.filterForm.patchValue(this.mapFiltersToForm(filters), { emitEvent: false });
  //     if (params['page'] || params['pageSize']) {
  //       const pageIndex = params['page'] ? parseInt(params['page'], 10) - 1 : 0;
  //       const pageSize = params['pageSize'] ? parseInt(params['pageSize'], 10) : (currentTab === LostSaleTabType.COMMERCIAL ? 100 : 100);

  //       if (currentTab === LostSaleTabType.COMMERCIAL) {
  //         this.commercialPageSubject.next({
  //           pageIndex,
  //           pageSize
  //         });
  //       } else {
  //         this.summaryPageSubject.next({
  //           pageIndex,
  //           pageSize
  //         });
  //       }
  //     }

  //     if (params['search']) {
  //       this.searchTermSubject.next(params['search']);
  //     }

  //     const isTabChange = currentTab !== previousTab;
  //     previousTab = currentTab;

  //     if (this.isInitializing) {
  //       this.isInitializing = false;
  //     }

  //     if (!this.isInitializing) {
  //       setTimeout(() => {
  //         this.queryParamsChangedSubject.next();
  //       }, 0);
  //     }
  //   });

  //   const urlTree = this.router.parseUrl(this.router.url);
  //   const params = urlTree.queryParams;
  //   const filters = this.parseFiltersFromQueryParams(params) || this.getDefaultFilters();

  //   this.filtersSubject.next(filters);
  //   this.stagedFiltersSubject.next(filters);
  //   this.filterForm.patchValue(this.mapFiltersToForm(filters), { emitEvent: false });

  //   if (params['search']) {
  //     this.searchTermSubject.next(params['search']);
  //   }

  //   if (params['page'] || params['pageSize']) {
  //     const pageIndex = params['page'] ? parseInt(params['page'], 10) - 1 : 0;
  //     const pageSize = params['pageSize'] ? parseInt(params['pageSize'], 10) : 100;
  //     const currentTab = this.activeTabSubject.value;

  //     if (currentTab === LostSaleTabType.COMMERCIAL) {
  //       this.commercialPageSubject.next({ pageIndex, pageSize });
  //     } else {
  //       this.summaryPageSubject.next({ pageIndex, pageSize });
  //     }
  //   }

  //   if (this.isInitializing) {
  //     setTimeout(() => {
  //       this.isInitializing = false;
  //       this.queryParamsChangedSubject.next();
  //     }, 100);
  //   }
  // }

  private setupFilterSync(): void {
    this.filterForm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(formValue => {
      const filters = this.mapFormToFilters(formValue);
      this.stagedFiltersSubject.next(filters);
    });
  }

  private mapFormToFilters(formValue: { start: Date; end: Date; companyId: number; salesOfficeId: number; subRouteId: number[]; assignedUserId: string[]; warehouseId: number[]; materialGroupId: string[]; materialId: string; customerId: number[]; salesGroup: string; salesZone: string }): LostSaleFilters {
    return {
      startDate: formValue.start,
      endDate: formValue.end,
      companyId: formValue.companyId,
      salesOfficeId: formValue.salesOfficeId,
      subRouteId: formValue.subRouteId || [],
      assignedUserId: formValue.assignedUserId || [],
      warehouseId: formValue.warehouseId || [],
      materialGroupId: formValue.materialGroupId || [],
      materialId: formValue.materialId,
      customerId: formValue.customerId || [],
      salesGroup: formValue.salesGroup ? [formValue.salesGroup] : [],
      salesZone: formValue.salesZone ? [formValue.salesZone] : []
    };
  }

  private mapFiltersToForm(filters: LostSaleFilters) {
    return {
      start: filters.startDate,
      end: filters.endDate,
      companyId: filters.companyId,
      salesOfficeId: filters.salesOfficeId,
      subRouteId: filters.subRouteId,
      assignedUserId: filters.assignedUserId,
      warehouseId: filters.warehouseId,
      materialGroupId: filters.materialGroupId,
      materialId: filters.materialId,
      customerId: filters.customerId,
      salesGroup: filters.salesGroup?.[0] || '',
      salesZone: filters.salesZone?.[0] || ''
    };
  }

  private parseFiltersFromQueryParams(params: Params): LostSaleFilters | null {
    try {
      const filters: LostSaleFilters = this.getDefaultFilters();

      if (params['startDate']) filters.startDate = new Date(params['startDate']);
      if (params['endDate']) filters.endDate = new Date(params['endDate']);
      if (params['companyId']) filters.companyId = parseInt(params['companyId'], 10);
      if (params['salesOfficeId']) filters.salesOfficeId = parseInt(params['salesOfficeId'], 10);
      if (params['subRouteId']) filters.subRouteId = JSON.parse(params['subRouteId']);
      if (params['assignedUserId']) filters.assignedUserId = JSON.parse(params['assignedUserId']);
      if (params['warehouseId']) filters.warehouseId = JSON.parse(params['warehouseId']);
      if (params['materialGroupId']) filters.materialGroupId = JSON.parse(params['materialGroupId']);
      if (params['materialId']) filters.materialId = params['materialId'];
      if (params['customerId']) filters.customerId = JSON.parse(params['customerId']);
      if (params['salesGroup']) filters.salesGroup = JSON.parse(params['salesGroup']);
      if (params['salesZone']) filters.salesZone = JSON.parse(params['salesZone']);

      return filters;
    } catch (error) {
      return null;
    }
  }

  private updateQueryParams(): void {
    const queryParams: Params = {};

    const filters = this.filtersSubject.value;
    if (filters.startDate) queryParams['startDate'] = filters.startDate.toISOString();
    if (filters.endDate) queryParams['endDate'] = filters.endDate.toISOString();
    if (filters.companyId) queryParams['companyId'] = filters.companyId;
    if (filters.salesOfficeId) queryParams['salesOfficeId'] = filters.salesOfficeId;
    if (filters.subRouteId?.length) queryParams['subRouteId'] = JSON.stringify(filters.subRouteId);
    if (filters.assignedUserId?.length) queryParams['assignedUserId'] = JSON.stringify(filters.assignedUserId);
    if (filters.warehouseId?.length) queryParams['warehouseId'] = JSON.stringify(filters.warehouseId);
    if (filters.materialGroupId?.length) queryParams['materialGroupId'] = JSON.stringify(filters.materialGroupId);
    if (filters.materialId) queryParams['materialId'] = filters.materialId;
    if (filters.customerId?.length) queryParams['customerId'] = JSON.stringify(filters.customerId);
    if (filters.salesGroup?.length) queryParams['salesGroup'] = JSON.stringify(filters.salesGroup);
    if (filters.salesZone?.length) queryParams['salesZone'] = JSON.stringify(filters.salesZone);

    const searchTerm = this.searchTermSubject.value;
    if (searchTerm) queryParams['search'] = searchTerm;

    const currentPage = this.activeTabSubject.value === LostSaleTabType.COMMERCIAL
      ? this.commercialPageSubject.value
      : this.summaryPageSubject.value;

    queryParams['page'] = currentPage.pageIndex + 1;
    queryParams['pageSize'] = currentPage.pageSize;

    const currentUrlTree = this.router.parseUrl(this.router.url);
    const currentPath = currentUrlTree.root.children['primary'].segments.map(s => s.path).join('/');

    this.router.navigate(['/' + currentPath], {
      queryParams,
      replaceUrl: true
    }).then(() => {
      this.queryParamsChangedSubject.next();
    });
  }

  public updateFilters(filters: Partial<LostSaleFilters>): void {
    const currentFilters = this.filtersSubject.value;
    this.filtersSubject.next({ ...currentFilters, ...filters });
  }

  public updateStagedFilters(filters: Partial<LostSaleFilters>): void {
    const currentStagedFilters = this.stagedFiltersSubject.value;
    this.stagedFiltersSubject.next({ ...currentStagedFilters, ...filters });
  }

  public applyFilters(): void {
    const stagedFilters = this.stagedFiltersSubject.value;
    this.filtersSubject.next(stagedFilters);

    const queryParams: Params = {};
    if (stagedFilters.startDate) queryParams['startDate'] = stagedFilters.startDate.toISOString();
    if (stagedFilters.endDate) queryParams['endDate'] = stagedFilters.endDate.toISOString();
    if (stagedFilters.companyId) queryParams['companyId'] = stagedFilters.companyId;
    if (stagedFilters.salesOfficeId) queryParams['salesOfficeId'] = stagedFilters.salesOfficeId;
    if (stagedFilters.subRouteId?.length) queryParams['subRouteId'] = JSON.stringify(stagedFilters.subRouteId);
    if (stagedFilters.assignedUserId?.length) queryParams['assignedUserId'] = JSON.stringify(stagedFilters.assignedUserId);
    if (stagedFilters.warehouseId?.length) queryParams['warehouseId'] = JSON.stringify(stagedFilters.warehouseId);
    if (stagedFilters.materialGroupId?.length) queryParams['materialGroupId'] = JSON.stringify(stagedFilters.materialGroupId);
    if (stagedFilters.materialId) queryParams['materialId'] = stagedFilters.materialId;
    if (stagedFilters.customerId?.length) queryParams['customerId'] = JSON.stringify(stagedFilters.customerId);
    if (stagedFilters.salesGroup?.length) queryParams['salesGroup'] = JSON.stringify(stagedFilters.salesGroup);
    if (stagedFilters.salesZone?.length) queryParams['salesZone'] = JSON.stringify(stagedFilters.salesZone);

    const searchTerm = this.searchTermSubject.value;
    if (searchTerm) queryParams['search'] = searchTerm;

    queryParams['page'] = 1;
    queryParams['pageSize'] = this.activeTabSubject.value === LostSaleTabType.COMMERCIAL
      ? this.commercialPageSubject.value.pageSize
      : this.summaryPageSubject.value.pageSize;

    if (this.activeTabSubject.value === LostSaleTabType.COMMERCIAL) {
      this.commercialPageSubject.next({ pageIndex: 0, pageSize: this.commercialPageSubject.value.pageSize });
    } else {
      this.summaryPageSubject.next({ pageIndex: 0, pageSize: this.summaryPageSubject.value.pageSize });
    }

    const currentUrlTree = this.router.parseUrl(this.router.url);
    const currentPath = currentUrlTree.root.children['primary'].segments.map(s => s.path).join('/');

    this.router.navigate(['/' + currentPath], {
      queryParams,
      replaceUrl: true
    });
  }

  public resetFilters(): void {
    const queryParams: Params = {};
    queryParams['page'] = 1;
    queryParams['pageSize'] = 100;

    const defaultFilters = this.getDefaultFilters();
    this.filterForm.patchValue(this.mapFiltersToForm(defaultFilters), { emitEvent: false });
    this.filtersSubject.next(defaultFilters);
    this.stagedFiltersSubject.next(defaultFilters);

    if (this.activeTabSubject.value === LostSaleTabType.COMMERCIAL) {
      this.commercialPageSubject.next({ pageIndex: 0, pageSize: 100 });
    } else {
      this.summaryPageSubject.next({ pageIndex: 0, pageSize: 100 });
    }

    const currentUrlTree = this.router.parseUrl(this.router.url);
    const currentPath = currentUrlTree.root.children['primary'].segments.map(s => s.path).join('/');

    this.router.navigate(['/' + currentPath], {
      queryParams,
      replaceUrl: true
    });
  }

  public setSearchTerm(term: string): void {
    this.searchTermSubject.next(term);
    this.updateQueryParams();
  }

  public setActiveTab(tab: LostSaleTabType): void {
    const route = tab === LostSaleTabType.COMMERCIAL ? 'commercial' : 'summary';
    this.router.navigate(['/reports/lost-sale-v2', route], {
      queryParamsHandling: 'preserve'
    });
  }

  public setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  public setCommercialData(data: LostSaleItem[], total: number): void {
    this.commercialDataSubject.next(data);
    this.commercialTotalSubject.next(total);
  }

  public setSummaryData(data: LostSaleSummaryItem[], total: number): void {
    this.summaryDataSubject.next(data);
    this.summaryTotalSubject.next(total);
  }

  public setCommercialPage(pageIndex: number, pageSize: number): void {
    const current = this.commercialPageSubject.value;
    if (current.pageIndex !== pageIndex || current.pageSize !== pageSize) {
      this.commercialPageSubject.next({ pageIndex, pageSize });
      this.updateQueryParams();
    }
  }

  public setSummaryPage(pageIndex: number, pageSize: number): void {
    const current = this.summaryPageSubject.value;
    if (current.pageIndex !== pageIndex || current.pageSize !== pageSize) {
      this.summaryPageSubject.next({ pageIndex, pageSize });
      this.updateQueryParams();
    }
  }

  public setCommercialSort(active: string, direction: 'asc' | 'desc' | ''): void {
    this.commercialSortSubject.next({ active, direction });
  }

  public setSummarySort(active: string, direction: 'asc' | 'desc' | ''): void {
    this.summarySortSubject.next({ active, direction });
  }

  public hasActiveFilters(): boolean {
    const currentFilters = this.filtersSubject.value;
    const defaultFilters = this.getDefaultFilters();

    return (
      currentFilters.companyId !== defaultFilters.companyId ||
      currentFilters.salesOfficeId !== defaultFilters.salesOfficeId ||
      currentFilters.subRouteId?.length !== defaultFilters.subRouteId?.length ||
      currentFilters.assignedUserId?.length !== defaultFilters.assignedUserId?.length ||
      currentFilters.warehouseId?.length !== defaultFilters.warehouseId?.length ||
      currentFilters.materialGroupId?.length !== defaultFilters.materialGroupId?.length ||
      currentFilters.materialId !== defaultFilters.materialId ||
      currentFilters.customerId?.length !== defaultFilters.customerId?.length ||
      currentFilters.salesGroup?.length !== defaultFilters.salesGroup?.length ||
      currentFilters.salesZone?.length !== defaultFilters.salesZone?.length ||
      this.searchTermSubject.value !== ''
    );
  }

  public hasPendingFilterChanges(): boolean {
    const appliedFilters = this.filtersSubject.value;
    const stagedFilters = this.stagedFiltersSubject.value;

    return JSON.stringify(appliedFilters) !== JSON.stringify(stagedFilters);
  }

  public getCurrentFilters(): LostSaleFilters {
    return this.filtersSubject.value;
  }

  public getCurrentSearchTerm(): string {
    return this.searchTermSubject.value;
  }

  public getCurrentCommercialSort(): { active: string; direction: 'asc' | 'desc' | '' } {
    return this.commercialSortSubject.value;
  }

  public getCurrentSummarySort(): { active: string; direction: 'asc' | 'desc' | '' } {
    return this.summarySortSubject.value;
  }

  public getCurrentCommercialPagination(): { pageIndex: number; pageSize: number } {
    return this.commercialPageSubject.value;
  }

  public getCurrentSummaryPagination(): { pageIndex: number; pageSize: number } {
    return this.summaryPageSubject.value;
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get loadingState(): boolean {
    return this.loadingSubject.value;
  }
}